import { useId, useMemo, useState, type FormEvent } from 'react';
import { calendarProblems, MAX_YEAR, MIN_YEAR, sameCalendar, teachingWeeks } from '../../core/calendar';
import type { Calendar, CalendarBreak } from '../../core/types';
import { plural } from './UnitRow';

/**
 * Each problem in words, and the teaching weeks when the dates are usable (0 otherwise). The
 * form is stricter than the Worker: a one-day year is a typo.
 */
function check(calendar: Calendar): { problems: string[]; weeks: number } {
  const label = (i: number) => calendar.breaks[i]?.name.trim() || `Break ${i + 1}`;
  const out = calendarProblems(calendar).map(code => {
    if (code === 'first_day_invalid') return 'Enter the first day of school.';
    if (code === 'last_day_invalid') return 'Enter the last day of school.';
    if (code === 'last_day_before_first_day') return 'The last day must be after the first day.';
    if (code === 'calendar_out_of_range') return `Use dates between ${MIN_YEAR} and ${MAX_YEAR}.`;
    if (code === 'calendar_span_invalid') return 'A school year can span at most two years.';
    const match = /^break_(\d+)_(invalid|end_before_start)$/.exec(code);
    if (match) return match[2] === 'invalid' ? `Enter both dates for ${label(Number(match[1]))}.` : `${label(Number(match[1]))} ends before it starts.`;
    return 'Check the dates.';
  });
  if (!out.length && calendar.lastDay === calendar.firstDay) out.push('The last day must be after the first day.');
  // Only counted when nothing else is wrong: an invalid date cannot be walked week by week.
  const weeks = out.length ? 0 : teachingWeeks(calendar).length;
  if (!out.length && weeks === 0) out.push('These dates leave no teaching weeks.');
  return { problems: out, weeks };
}

/** Another of the teacher's plans this calendar could also be saved to (R3). */
export interface CalendarChoice { id: string; label: string; calendar: Calendar }

/** The choices ticked by default: those whose calendar is identical to this one before any edit (R3). */
export const defaultTicks = (choices: readonly CalendarChoice[], before: Calendar): string[] =>
  choices.filter(c => sameCalendar(c.calendar, before)).map(c => c.id);

/**
 * The school year: first day, last day, and named breaks or testing windows (R4). It opens
 * with the dates it is given — typical ones for a new plan — and every date is editable. Given
 * `others`, it offers to save the same calendar to them, and submits the ticked plan ids too.
 */
export function CalendarForm({ initial, others, submitLabel, busy = false, onSubmit, onCancel }: {
  initial: Calendar; others?: readonly CalendarChoice[]; submitLabel: string; busy?: boolean;
  onSubmit(calendar: Calendar, alsoPlanIds?: string[]): void; onCancel?(): void;
}) {
  const id = useId();
  const [calendar, setCalendar] = useState<Calendar>(initial);
  // Ticks are the defaults with the teacher's own toggles flipped, so choices that arrive late still default right.
  const [toggled, setToggled] = useState<readonly string[]>([]);
  const same = useMemo(() => defaultTicks(others ?? [], initial), [others, initial]);
  const ticked = (others ?? []).map(o => o.id).filter(planId => same.includes(planId) !== toggled.includes(planId));
  const toggle = (planId: string) => setToggled(t => (t.includes(planId) ? t.filter(x => x !== planId) : [...t, planId]));
  const [errors, setErrors] = useState<string[]>([]);
  const setBreak = (i: number, change: Partial<CalendarBreak>) =>
    setCalendar(c => ({ ...c, breaks: c.breaks.map((b, k) => (k === i ? { ...b, ...change } : b)) }));
  const { problems, weeks } = check(calendar);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setErrors(problems);
    if (problems.length) return;
    const saved = { ...calendar, breaks: calendar.breaks.map((b, i) => ({ ...b, name: b.name.trim() || `Break ${i + 1}` })) };
    if (others) onSubmit(saved, ticked); else onSubmit(saved);
  };
  return <form className="setup-form calendar-form" onSubmit={submit} noValidate>
    <div className="profile-grid">
      <div><label htmlFor={`${id}-first`}>First day</label><input id={`${id}-first`} type="date" value={calendar.firstDay} onChange={e => setCalendar(c => ({ ...c, firstDay: e.target.value }))} /></div>
      <div><label htmlFor={`${id}-last`}>Last day</label><input id={`${id}-last`} type="date" value={calendar.lastDay} onChange={e => setCalendar(c => ({ ...c, lastDay: e.target.value }))} /></div>
    </div>
    <h3 className="form-subheading">Breaks and testing windows</h3>
    <p className="muted">Weeks wholly inside one are skipped. Testing windows count as weeks with no teaching.</p>
    {calendar.breaks.map((b, i) => <fieldset key={i} className="break-row" aria-label={`Break ${i + 1}`}>
      <div><label htmlFor={`${id}-b${i}-name`}>Name</label><input id={`${id}-b${i}-name`} type="text" maxLength={60} value={b.name} onChange={e => setBreak(i, { name: e.target.value })} /></div>
      <div><label htmlFor={`${id}-b${i}-start`}>Starts</label><input id={`${id}-b${i}-start`} type="date" value={b.start} onChange={e => setBreak(i, { start: e.target.value })} /></div>
      <div><label htmlFor={`${id}-b${i}-end`}>Ends</label><input id={`${id}-b${i}-end`} type="date" value={b.end} onChange={e => setBreak(i, { end: e.target.value })} /></div>
      <div><label htmlFor={`${id}-b${i}-kind`}>Type</label><select id={`${id}-b${i}-kind`} value={b.kind} onChange={e => setBreak(i, { kind: e.target.value as CalendarBreak['kind'] })}><option value="break">Break</option><option value="testing">Testing window</option></select></div>
      <button className="text-button" type="button" aria-label={`Remove ${b.name.trim() || `break ${i + 1}`}`} onClick={() => setCalendar(c => ({ ...c, breaks: c.breaks.filter((_, k) => k !== i) }))}>Remove</button>
    </fieldset>)}
    <button className="secondary" type="button" onClick={() => setCalendar(c => ({ ...c, breaks: [...c.breaks, { name: '', start: '', end: '', kind: 'break' }] }))}>Add a break or testing window</button>
    {others && others.length > 0 && <fieldset className="also-calendar">
      <legend className="form-subheading">Also use this calendar for</legend>
      {others.map(o => {
        const hint = !same.includes(o.id) && !ticked.includes(o.id) ? `${id}-also-${o.id}-hint` : undefined;
        return <div key={o.id} className="also-calendar-choice">
          <input id={`${id}-also-${o.id}`} type="checkbox" checked={ticked.includes(o.id)} onChange={() => toggle(o.id)} aria-describedby={hint} />
          {' '}<label htmlFor={`${id}-also-${o.id}`}>{o.label}</label>
          {hint && <>{' '}<span id={hint} className="muted">Different calendar</span></>}
        </div>;
      })}
    </fieldset>}
    {errors.length > 0 && <div className="error-message" role="alert">{errors.map(e => <p key={e}>{e}</p>)}</div>}
    <div className="form-footer">
      <button className="primary" type="submit" disabled={busy}>{submitLabel}</button>
      <span className="quota" role="status">{problems.length ? 'Check the dates above.' : `${plural(weeks, 'teaching week')} in this calendar`}</span>
      {onCancel && <button className="text-button" type="button" onClick={onCancel}>Cancel</button>}
    </div>
  </form>;
}
